import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import { ArrowLeft, FileText, Pill, Award, Scan, User, AlertTriangle, ShieldCheck, ExternalLink, Clock, Copy, Check, Lock, Key } from 'lucide-react';
import { doctorsApi, medicalRecordsApi } from '@/api';
import { usePatientAllergies } from '@/hooks';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { Button } from '@/components/ui/Button';
import type { MedicalRecord, Prescription, Certificate, ExamRequest } from '@/api';

interface PatientData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  cpf: string;
  birthDate: string;
  gender?: string;
  totalConsultations: number;
  lastConsultation?: string;
  healthConditions?: string[];
  allergies?: string[];
}

export function DoctorPatientRecordsPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const { data: patient, isLoading: patientLoading } = useQuery<PatientData>({
    queryKey: ['doctor', 'patient', patientId],
    queryFn: async () => {
      const response = await doctorsApi.getMyPatients(1, 100);
      const found = response.patients.find((p) => p.id === patientId);
      if (!found) throw new Error('Patient not found');
      return found;
    },
    enabled: !!patientId,
  });

  const { data: allergies } = usePatientAllergies(patientId);

  const { data: medicalRecords, isLoading: recordsLoading } = useQuery<MedicalRecord[]>({
    queryKey: ['medical-records', 'patient', patientId],
    queryFn: () => medicalRecordsApi.getPatientMedicalHistory(patientId!),
    enabled: !!patientId,
  });

  const isLoading = patientLoading || recordsLoading;

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatCpf = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  if (isLoading) {
    return <LoadingOverlay message="Carregando prontuário..." />;
  }

  if (!patient || !patientId) {
    return (
      <div className="container max-w-4xl py-6">
        <ErrorRetry
          message="Paciente não encontrado"
          onRetry={() => navigate('/medico/pacientes')}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/medico/pacientes')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5" />
          Voltar
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <FileText className="h-7 w-7 text-blue-600" />
          Prontuário de {patient.name}
        </h1>
        <div className="h-1 w-48 bg-blue-600 mt-2 rounded" />
      </div>

      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-gray-500" />
            Dados do Paciente
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Nome</p>
              <p className="font-medium">{patient.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">CPF</p>
              <p className="font-medium">{formatCpf(patient.cpf)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Data de Nascimento</p>
              <p className="font-medium">
                {formatDate(patient.birthDate)} ({calculateAge(patient.birthDate)} anos)
              </p>
            </div>
            {patient.gender && (
              <div>
                <p className="text-sm text-gray-500">Gênero</p>
                <p className="font-medium">{patient.gender}</p>
              </div>
            )}
          </div>

          {(allergies?.length ?? 0) > 0 && (
            <div className="mt-4 pt-4 border-t">
              <h3 className="text-sm font-medium text-red-600 flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4" />
                Alergias
              </h3>
              <div className="flex flex-wrap gap-2">
                {allergies?.map((allergy, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full"
                  >
                    {allergy.allergen}
                    {allergy.severity && ` (${allergy.severity})`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {patient.allergies && patient.allergies.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <h3 className="text-sm font-medium text-red-600 flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4" />
                Alergias (cadastradas)
              </h3>
              <div className="flex flex-wrap gap-2">
                {patient.allergies.map((allergy, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full"
                  >
                    {allergy}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Banner de Integração Oficial MeMed */}
      <div className="rounded-2xl border border-blue-500/25 bg-blue-50/60 dark:bg-blue-950/40 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-black text-lg shrink-0 shadow-sm">
            M
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-sm sm:text-base text-foreground">
                Prontuário com Certificação Digital MeMed
              </h3>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                <ShieldCheck className="w-3 h-3" />
                ICP-Brasil
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Prescrições, atestados e pedidos de exames emitidos neste prontuário possuem validade jurídica nacional e envio automático ao paciente via WhatsApp/SMS.
            </p>
          </div>
        </div>

        <Button
          onClick={() => navigate(`/medico/receitas/novo?patientId=${patientId}`)}
          className="shrink-0 rounded-xl font-bold text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm gap-2"
        >
          <Pill className="w-4 h-4" />
          <span>Prescrever via MeMed</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button
          variant="outline"
          className="flex items-center gap-2 justify-center rounded-xl font-semibold hover:border-blue-500 hover:text-blue-600 transition-colors h-11"
          onClick={() => navigate(`/medico/receitas/novo?patientId=${patientId}`)}
        >
          <Pill className="h-4 w-4 text-blue-600" />
          <span>Nova Receita (MeMed)</span>
        </Button>
        <Button
          variant="outline"
          className="flex items-center gap-2 justify-center rounded-xl font-semibold hover:border-emerald-500 hover:text-emerald-600 transition-colors h-11"
          onClick={() => navigate(`/medico/atestados/novo?patientId=${patientId}`)}
        >
          <Award className="h-4 w-4 text-emerald-600" />
          <span>Novo Atestado</span>
        </Button>
        <Button
          variant="outline"
          className="flex items-center gap-2 justify-center rounded-xl font-semibold hover:border-purple-500 hover:text-purple-600 transition-colors h-11"
          onClick={() => navigate(`/medico/exames/novo?patientId=${patientId}`)}
        >
          <Scan className="h-4 w-4 text-purple-600" />
          <span>Novo Pedido de Exames</span>
        </Button>
      </div>

      {!medicalRecords || medicalRecords.length === 0 ? (
        <EmptyState
          icon="FileText"
          title="Nenhum prontuário encontrado"
          description="Este paciente ainda não possui prontuários médicos."
          action={{ label: 'Voltar', onClick: () => navigate('/medico/pacientes') }}
        />
      ) : (
        <div className="space-y-6">
          {medicalRecords.map((record) => (
            <MedicalRecordCard
              key={record.id}
              record={record}
              onViewRecord={() => navigate(`/medico/prontuarios/${record.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface MedicalRecordCardProps {
  record: MedicalRecord;
  onViewRecord: () => void;
}

function MedicalRecordCard({ record, onViewRecord }: MedicalRecordCardProps) {
  const navigate = useNavigate();
  const { data: prescriptions } = useQuery<Prescription[]>({
    queryKey: ['medical-records', 'record', record.id, 'prescriptions'],
    queryFn: () => medicalRecordsApi.getPrescriptionsByMedicalRecord(record.id),
    enabled: !!record.id,
  });

  const { data: certificates } = useQuery<Certificate[]>({
    queryKey: ['medical-records', 'record', record.id, 'certificates'],
    queryFn: () => medicalRecordsApi.getCertificatesByMedicalRecord(record.id),
    enabled: !!record.id,
  });

  const { data: examRequests } = useQuery<ExamRequest[]>({
    queryKey: ['medical-records', 'record', record.id, 'exam-requests'],
    queryFn: () => medicalRecordsApi.getExamRequestsByMedicalRecord(record.id),
    enabled: !!record.id,
  });

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800',
    SIGNED: 'bg-purple-100 text-purple-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };

  const statusLabels: Record<string, string> = {
    DRAFT: 'Rascunho',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Completo',
    SIGNED: 'Assinado',
    CANCELLED: 'Cancelado',
  };

  return (
    <Card className="rounded-2xl border border-border bg-card shadow-xs hover:shadow-md transition-all overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-border/70">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-base text-foreground">
                Prontuário #{record.id.slice(0, 8)}
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded-md border border-blue-200/60 dark:border-blue-800/60">
                <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                MeMed Conectado
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Criado em {formatDate(record.createdAt)}
            </p>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-bold w-fit ${
              statusColors[record.status] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {statusLabels[record.status] || record.status}
          </span>
        </div>

        <div className="space-y-4">
          {prescriptions && prescriptions.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2.5">
                <Pill className="h-4 w-4 text-primary" />
                <span>Receitas Médicas Emitidas ({prescriptions.length})</span>
              </h4>
              <div className="space-y-3">
                {prescriptions.map((prescription) => (
                  <PrescriptionItemRow
                    key={prescription.id}
                    prescription={prescription}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}

          {certificates && certificates.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2.5">
                <Award className="h-4 w-4 text-emerald-600" />
                <span>Atestados Médicos ({certificates.length})</span>
              </h4>
              <div className="space-y-2">
                {certificates.map((certificate) => {
                  const targetPdf = certificate.pdfUrl || certificate.memedPdfUrl;

                  return (
                    <div
                      key={certificate.id}
                      className="p-3.5 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-800/40 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs font-mono font-bold text-foreground">
                            Atestado #{certificate.id.slice(0, 8)}
                          </p>
                          <span className="text-[11px] text-muted-foreground">
                            · {formatDate(certificate.createdAt)}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-full border border-emerald-300/60 dark:border-emerald-800">
                            <ShieldCheck className="w-3 h-3 text-emerald-600" />
                            Assinado Digitalmente (ICP-Brasil)
                          </span>
                        </div>
                        <p className="text-xs text-foreground font-semibold mt-1">
                          {certificate.type === 'SICK_NOTE'
                            ? 'Atestado de afastamento'
                            : certificate.type === 'HEALTH_CERTIFICATE'
                            ? 'Atestado de saúde'
                            : 'Atestado médico'}{' '}
                          - {certificate.daysOff ? `${certificate.daysOff} dias de repouso` : 'Sem afastamento'}
                        </p>
                      </div>

                      {targetPdf && (
                        <button
                          type="button"
                          onClick={() => window.open(targetPdf, '_blank', 'noopener,noreferrer')}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-card border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 text-xs font-bold rounded-lg shadow-2xs shrink-0 cursor-pointer"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span>Ver Atestado Oficial</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {examRequests && examRequests.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2.5">
                <Scan className="h-4 w-4 text-purple-600" />
                <span>Pedidos de Exame ({examRequests.length})</span>
              </h4>
              <div className="space-y-2">
                {examRequests.map((exam) => {
                  const targetPdf = exam.pdfUrl || exam.memedPdfUrl;

                  return (
                    <div
                      key={exam.id}
                      className="p-3.5 bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200/60 dark:border-purple-800/40 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs font-mono font-bold text-foreground">
                            Pedido #{exam.id.slice(0, 8)}
                          </p>
                          <span className="text-[11px] text-muted-foreground">
                            · {formatDate(exam.createdAt)}
                          </span>
                          <span className="text-[10px] font-bold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/50 px-2 py-0.5 rounded-full border border-purple-300/60 dark:border-purple-800">
                            Prioridade: {exam.priority === 'URGENT' ? 'Urgente' : exam.priority === 'EMERGENCY' ? 'Emergência' : 'Rotina'}
                          </span>
                        </div>
                        <p className="text-xs text-foreground font-semibold mt-1 truncate">
                          {exam.exams.map((e) => e.examName).join(', ') || 'Exame Laboratorial / Imagem'}
                        </p>
                      </div>

                      {targetPdf && (
                        <button
                          type="button"
                          onClick={() => window.open(targetPdf, '_blank', 'noopener,noreferrer')}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-card border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 text-xs font-bold rounded-lg shadow-2xs shrink-0 cursor-pointer"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span>Ver Pedido de Exame</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-border/70 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onViewRecord}
            className="rounded-xl font-bold text-xs h-10 border-border hover:border-primary hover:text-primary transition-colors gap-1.5"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Ver Prontuário Completo</span>
          </Button>

          <Button
            size="sm"
            onClick={() => {
              const params = new URLSearchParams();
              if (record.patientId) params.set('patientId', record.patientId);
              if (record.appointmentId) params.set('appointmentId', record.appointmentId);
              navigate(`/medico/receitas/novo?${params.toString()}`);
            }}
            className="rounded-xl font-bold text-xs h-10 bg-blue-600 hover:bg-blue-700 text-white shadow-xs gap-1.5"
          >
            <Pill className="w-3.5 h-3.5" />
            <span>Prescrever para este Prontuário (MeMed)</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PrescriptionItemRow({
  prescription,
  formatDate,
}: {
  prescription: Prescription;
  formatDate: (dateStr: string) => string;
}) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPin, setCopiedPin] = useState(false);
  const [fetchedData, setFetchedData] = useState<{ link?: string; unlockCode?: string } | null>(null);
  const [isFetchingLink, setIsFetchingLink] = useState(false);

  const initialLink =
    prescription.digitalPrescription?.link ||
    prescription.content?.digitalPrescription?.link ||
    prescription.digitalPrescriptionLink ||
    prescription.memedPdfUrl ||
    prescription.pdfUrl ||
    null;

  const initialUnlockCode =
    prescription.digitalPrescription?.unlockCode ||
    prescription.content?.digitalPrescription?.unlockCode ||
    prescription.unlockCode ||
    null;

  const currentLink = fetchedData?.link || initialLink;
  const currentUnlockCode = fetchedData?.unlockCode || initialUnlockCode;
  const targetPdf = prescription.memedPdfUrl || prescription.pdfUrl;
  const isMemedSigned = Boolean(
    prescription.signedAt ||
    prescription.memedPrescriptionId ||
    prescription.memedStatus === 'SIGNED' ||
    currentUnlockCode ||
    currentLink
  );

  const handleFetchLink = async () => {
    if (!prescription.id) return;
    setIsFetchingLink(true);
    try {
      const res = await medicalRecordsApi.getDigitalPrescriptionLink(prescription.id);
      if (res?.link || res?.unlockCode) {
        setFetchedData({ link: res.link, unlockCode: res.unlockCode });
      }
    } catch {
      // Ignora erro se não encontrar
    } finally {
      setIsFetchingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (!currentLink) return;
    navigator.clipboard?.writeText(currentLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyPin = () => {
    if (!currentUnlockCode) return;
    navigator.clipboard?.writeText(currentUnlockCode);
    setCopiedPin(true);
    setTimeout(() => setCopiedPin(false), 2000);
  };

  return (
    <div className="p-4 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-800/40 rounded-xl space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs font-mono font-bold text-foreground">
            Receita #{prescription.id.slice(0, 8)}
          </p>
          <span className="text-[11px] text-muted-foreground">
            · {formatDate(prescription.createdAt)}
          </span>
          {isMemedSigned ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-full border border-emerald-300/60 dark:border-emerald-800">
              <ShieldCheck className="w-3 h-3 text-emerald-600" />
              Assinada via MeMed (ICP-Brasil)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 rounded-full border border-amber-300/60 dark:border-amber-800">
              <Clock className="w-3 h-3 text-amber-600" />
              Sincronização MeMed Pendente
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {targetPdf && (
            <button
              type="button"
              onClick={() => window.open(targetPdf, '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-card border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 text-xs font-bold rounded-lg shadow-2xs cursor-pointer"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Ver PDF</span>
            </button>
          )}
        </div>
      </div>

      {/* Bloco de Receita Digital: Link e PIN de Desbloqueio */}
      {currentLink || currentUnlockCode ? (
        <div className="p-3 bg-white/90 dark:bg-card/90 rounded-lg border border-blue-200/80 dark:border-blue-900/60 space-y-2 text-xs shadow-2xs">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-blue-800 dark:text-blue-300 font-bold">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Receita Digital Oficial</span>
            </div>
            {currentUnlockCode && (
              <div className="flex items-center gap-1.5 bg-neutral-100 dark:bg-neutral-800/80 px-2.5 py-1 rounded-md border border-neutral-200 dark:border-neutral-700">
                <Lock className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-muted-foreground font-medium">PIN de Desbloqueio:</span>
                <span className="font-mono font-bold text-foreground text-sm tracking-wide">
                  {currentUnlockCode}
                </span>
                <button
                  type="button"
                  onClick={handleCopyPin}
                  title="Copiar PIN"
                  className="ml-1 text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                >
                  {copiedPin ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            )}
          </div>

          {currentLink && (
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-neutral-100 dark:border-neutral-800">
              <span className="text-[11px] text-muted-foreground truncate max-w-xs sm:max-w-md font-mono">
                {currentLink}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 cursor-pointer"
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedLink ? 'Copiado' : 'Copiar Link'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => window.open(currentLink, '_blank', 'noopener,noreferrer')}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md bg-blue-600 hover:bg-blue-700 text-white cursor-pointer shadow-2xs"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Abrir Receita</span>
                </button>
              </div>
            </div>
          )}
        </div>
      ) : prescription.memedPrescriptionId ? (
        <div className="flex items-center justify-between p-2.5 bg-white/70 dark:bg-card/70 rounded-lg border border-blue-100 dark:border-blue-900/40 text-xs">
          <span className="text-muted-foreground">
            Receita emitida via MeMed Sinapse.
          </span>
          <button
            type="button"
            disabled={isFetchingLink}
            onClick={handleFetchLink}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded font-semibold hover:bg-blue-100 cursor-pointer text-xs"
          >
            <Key className="w-3.5 h-3.5" />
            <span>{isFetchingLink ? 'Carregando...' : 'Consultar Link e PIN'}</span>
          </button>
        </div>
      ) : null}

      {/* Medicamentos ricos */}
      <div className="space-y-2">
        {prescription.medications.map((med, idx) => {
          const medName = med.nome || med.name;
          const posology =
            med.sanitized_posology ||
            med.posologia ||
            (med.dose && med.frequency ? `${med.dose} - ${med.frequency}` : med.frequency || med.dose);
          const presentationInfo = med.presentation || med.descricao;
          const quantityText =
            med.quantidade !== undefined
              ? `${med.quantidade} ${med.unidade || 'unidades'}`
              : med.quantity !== undefined
              ? `${med.quantity} unidades`
              : null;

          return (
            <div
              key={idx}
              className="p-2.5 bg-white dark:bg-card rounded-lg border border-blue-100 dark:border-neutral-800 text-xs space-y-1"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-foreground text-sm">{medName}</span>
                  {med.controle_especial && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 rounded border border-amber-200">
                      Controle Especial
                    </span>
                  )}
                  {med.receituario && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 rounded border border-blue-200">
                      {med.receituario}
                    </span>
                  )}
                </div>
                {med.fabricante && (
                  <span className="text-[11px] text-muted-foreground font-medium">
                    {med.fabricante}
                  </span>
                )}
              </div>

              {med.principio_ativo && (
                <p className="text-muted-foreground">
                  <span className="font-semibold text-foreground/80">Princípio ativo:</span> {med.principio_ativo}
                </p>
              )}

              <p className="text-muted-foreground">
                {[presentationInfo, med.concentration, posology].filter(Boolean).join(' · ')}
              </p>

              {(quantityText || med.duration) && (
                <p className="text-[11px] text-muted-foreground">
                  {quantityText} {med.duration ? `— ${med.duration}` : ''}
                </p>
              )}

              {med.observations && (
                <p className="italic text-muted-foreground bg-neutral-50 dark:bg-neutral-900 p-1.5 rounded">
                  Obs: {med.observations}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
